import { LifecycleTemplate } from '@eagle/core-data-types';
import { Badge, IconButton, Tooltip } from '@ethings/ui-toolkit';
import { Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import { FC, memo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmallScreen } from '../../hooks';
import { BottomNavigationAction } from '../bottom-navigation';
import { Icon } from '../icons';
import { SelfClaimDialog } from './self-claim-dialog';

interface Props {
  disabled?: boolean;
  onSelfClaimClicked: () => void;
  onSuccess?: (formData: Record<string, string>) => void;
  openSelfClaim?: boolean;
  size?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
  lifecycleTemplate?: LifecycleTemplate;
  stakeholderRole?: string | null;
  showIcon?: boolean;
}

export const SelfClaimBar: FC<Props> = memo(({ disabled = false, onSelfClaimClicked: onClickHandler, onSuccess, openSelfClaim = false, lifecycleTemplate, stakeholderRole, showIcon = true }) => {
  const { t } = useTranslation(['common']);
  const smallScreen = useSmallScreen();

  const handleSelfClaimDialogNext = (formData: Record<string, string>): void => {
    onSuccess?.(formData);
  };

  const renderSelfClaimButton = (): JSX.Element => {
    if (smallScreen) {
      return <BottomNavigationAction
        data-testid='self-claim-button'
        key={'extraActions'}
        icon={<Icon icon="SelfClaimIcon" />}
        label={t('admin:page.transfers-list.update-stage-dialog.self-claim.title')}
        onClick={onClickHandler}
      />;
    }

    return <Badge data-testid="self-claim-badge">
      <Tooltip arrow disableHoverListener={disabled} title={t('admin:page.transfers-list.update-stage-dialog.self-claim.vehicle-search.title')} >
        <span>
          <IconButton
            color='primary'
            data-testid="self-claim-button"
            disabled={disabled}
            onClick={onClickHandler}
          >
            <Icon icon="SelfClaimIcon" fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Badge>;
  };

  return (
    <>
      {showIcon && renderSelfClaimButton()}

      {openSelfClaim && lifecycleTemplate && stakeholderRole && (
        <SelfClaimDialog
          open={openSelfClaim}
          lifecycleTemplate={lifecycleTemplate}
          onSelfClaimDialogClose={onClickHandler}
          onSelfClaimDialogNext={handleSelfClaimDialogNext}
          stakeholderRole={stakeholderRole}
        />
      )}
    </>
  );
});

SelfClaimBar.displayName = 'SelfClaimBar';
